<template>
  <div class="admin-surveys-wrap">
    <main class="page">

      <p class="breadcrumb">SEQ / Admin / Surveys</p>

      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Surveys</h1>
          <p class="page-subtitle">Manage the survey campaigns that run your questionnaires.</p>
        </div>
        <button
          type="button"
          class="btn btn-primary new-button"
          @click="openCreate"
        >
          <svg class="plus-icon" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          New survey
        </button>
      </div>

      <div class="meta-strip">
        <div class="meta-item">
          <span class="meta-label">Organization</span>
          <span class="meta-value">{{ organizationName }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Total</span>
          <span class="meta-value">{{ surveys.length }}</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">Collecting now</span>
          <span class="meta-value">{{ collectingCount }}</span>
        </div>
      </div>

      <section class="section">
        <h2 class="section-title">All surveys</h2>

        <p
          v-if="loadError"
          class="error-banner"
        >{{ loadError }}</p>

        <div
          v-if="loading"
          class="loading-state"
          role="status"
          aria-live="polite"
        >
          <span class="spinner" aria-hidden="true"></span>
          <p class="loading-text">Loading surveys<span class="loading-dots"><i>.</i><i>.</i><i>.</i></span></p>
        </div>

        <p
          v-else-if="surveys.length === 0"
          class="empty-state"
        >
          No surveys yet. Create your first one to get started.
        </p>

        <template v-else>
          <div
            v-for="group in surveyGroups"
            :key="group.key"
            class="survey-group"
          >
            <div class="group-header">
              <span class="group-dot" :class="`dot-${group.key}`" aria-hidden="true"></span>
              <h3 class="group-title">{{ group.title }}</h3>
              <span class="group-count">{{ group.items.length }}</span>
              <span class="group-hint">{{ group.hint }}</span>
            </div>

            <div class="survey-list">
              <button
                v-for="s in group.items"
                :key="s.id"
                type="button"
                class="survey-row"
                @click="openSurvey(s)"
              >
                <div class="row-main">
                  <div class="row-name">{{ s.name }}</div>
                  <div class="row-slug">{{ s.slug }}</div>
                </div>
                <div class="row-meta">
                  <span class="status-pill" :class="`status-${s.status}`">{{ s.status }}</span>
                  <span class="row-window">{{ formatTimeWindow(s.starts_at, s.ends_at) }}</span>
                  <span class="row-date">{{ formatDateLong(s.created_at) }}</span>
                  <span class="row-chevron" aria-hidden="true">›</span>
                </div>
              </button>
            </div>
          </div>
        </template>
      </section>

    </main>

    <SurveyFormModal
      v-if="showCreate"
      mode="create"
      :server-error="createError"
      @submit="onCreateSubmit"
      @cancel="onCreateCancel"
    />
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import {
  surveysState,
  formatTimeWindow,
  formatDateLong,
  dateInputToIso,
} from './_data/surveys.js';
import { questionnairesState } from './_data/questionnaires.js';
import SurveyFormModal from './SurveyFormModal.vue';

export default {
  name: 'AdminSurveyList',
  components: { SurveyFormModal },
  data() {
    return {
      organizationName: 'Berner Fachhochschule',
      showCreate: false,
      createError: null,
      loading: true,
      loadError: null,
    };
  },
  computed: {
    surveys() {
      return surveysState.items;
    },

    /**
     * Surveys grouped by where they sit in time, because "which of these is
     * actually collecting right now" is the question this page gets asked
     * and a flat list stops answering it once there are more than a few.
     *
     * The grouping deliberately mirrors the reasons the backend gives a
     * participant when a link does not work (see get_survey_runtime:
     * not_active, not_started, ended). Same concept, same boundaries, so an
     * admin looking at this list and a participant holding a link never
     * disagree about whether a survey is open.
     *
     * Status alone is not enough: an `active` survey whose window has not
     * opened is not collecting, and one whose window has passed is not
     * either, regardless of what the status column says.
     */
    surveyGroups() {
      const now = Date.now();
      const at = v => (v ? new Date(v).getTime() : null);

      const collecting = [];
      const scheduled = [];
      const finished = [];
      const drafts = [];

      for (const s of this.surveys) {
        const starts = at(s.starts_at);
        const ends = at(s.ends_at);

        if (s.status === 'closed' || s.status === 'archived') {
          finished.push(s);
        } else if (s.status !== 'active') {
          drafts.push(s);
        } else if (starts && starts > now) {
          scheduled.push(s);
        } else if (ends && ends < now) {
          // Open by status but past its window. Worth separating rather
          // than showing as collecting, because it is not.
          finished.push(s);
        } else {
          collecting.push(s);
        }
      }

      const byStart = (a, b) => (at(a.starts_at) || 0) - (at(b.starts_at) || 0);
      const byEndDesc = (a, b) => (at(b.ends_at) || at(b.created_at) || 0)
        - (at(a.ends_at) || at(a.created_at) || 0);
      const byCreatedDesc = (a, b) => at(b.created_at) - at(a.created_at);

      return [
        {
          key: 'collecting',
          title: 'Collecting now',
          hint: 'Open to participants',
          items: collecting.sort(byStart),
        },
        {
          key: 'scheduled',
          title: 'Scheduled',
          hint: 'Open, but the start date has not arrived',
          items: scheduled.sort(byStart),
        },
        {
          key: 'draft',
          title: 'Not opened',
          hint: 'Draft. The participant link does nothing yet',
          items: drafts.sort(byCreatedDesc),
        },
        {
          key: 'finished',
          title: 'Finished',
          hint: 'Closed, archived, or past the end date',
          items: finished.sort(byEndDesc),
        },
      ].filter(g => g.items.length > 0);
    },

    collectingCount() {
      const g = this.surveyGroups.find(x => x.key === 'collecting');
      return g ? g.items.length : 0;
    },
  },
  async created() {
    // GET /api/seq/surveys. The backend JOINs seq_questionnaires and
    // filters by the caller's org. On success replace the shared
    // store. On failure keep whatever is in the store (the dummy
    // seed) so dev-without-backend still renders something.
    //
    // Questionnaires are fetched too, and must be: the create modal's
    // dropdown reads questionnairesState, which is otherwise still the
    // local dummy seed on this page. Picking a seeded entry sent an id
    // that does not exist in the database, and the create failed with a
    // 400 that named the wrong cause. The dropdown has to be real data
    // or the form cannot produce a valid request.
    const [surveys, questionnaires] = await Promise.allSettled([
      apiService.fetchSurveys(),
      apiService.fetchQuestionnaires(),
    ]);

    if (surveys.status === 'fulfilled') {
      surveysState.items = surveys.value;
      this.loadError = null;
    } else {
      console.error('Failed to fetch surveys from backend:', surveys.reason);
      this.loadError = 'Could not load surveys from the server. Showing local data.';
    }

    if (questionnaires.status === 'fulfilled') {
      questionnairesState.items = questionnaires.value;
    } else {
      console.error('Failed to fetch questionnaires:', questionnaires.reason);
      this.loadError = 'Could not load questionnaires. Creating a survey will not work until this succeeds.';
    }

    this.loading = false;
  },
  methods: {
    openSurvey(s) {
      this.$router.push(`/admin/surveys/${s.slug}`);
    },
    openCreate() {
      this.createError = null;
      this.showCreate = true;
    },
    onCreateCancel() {
      this.showCreate = false;
      this.createError = null;
    },
    async onCreateSubmit(payload) {
      // POST /api/seq/surveys. Form modal sends starts_at / ends_at as
      // YYYY-MM-DD strings (or empty); convert to ISO at the boundary.
      this.createError = null;
      const body = {
        questionnaire_id: payload.questionnaire_id,
        name: payload.name,
        slug: payload.slug,
        description: payload.description,
        starts_at: dateInputToIso(payload.starts_at),
        ends_at: dateInputToIso(payload.ends_at),
        status: payload.status,
      };
      try {
        const created = await apiService.createSurvey(body);
        surveysState.items.unshift(created);
        this.showCreate = false;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (Array.isArray(detail)) {
          this.createError = detail.map(d => d.msg).join(' ');
        } else if (typeof detail === 'string') {
          this.createError = detail;
        } else {
          this.createError = 'Failed to create survey. Please try again.';
        }
      }
    },
    formatTimeWindow,
    formatDateLong,
  },
};
</script>

<style scoped>
.admin-surveys-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.admin-surveys-wrap *,
.admin-surveys-wrap *::before,
.admin-surveys-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 820px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.page-header-text {
  flex: 1;
  min-width: 240px;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 17px;
  color: var(--color-text-secondary);
  margin: 0;
  font-weight: 400;
}

.btn {
  padding: 10px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s;
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.new-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  white-space: nowrap;
  margin-top: 4px;
}

.plus-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.meta-strip {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 14px 18px;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  margin-bottom: 2.5rem;
  font-size: 13px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-label {
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
  font-weight: 500;
}

.meta-value {
  color: var(--color-text-primary);
  font-weight: 500;
}

.section {
  margin-bottom: 2.5rem;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 1rem;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--color-accent);
  display: inline-block;
}

.empty-state {
  font-size: 14px;
  color: var(--color-text-tertiary);
  background: var(--color-bg-soft);
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: 32px 20px;
  text-align: center;
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 32px 0;
  font-size: 13px;
  color: var(--color-text-tertiary);
}

.loading-text {
  margin: 0;
}

/* Spinning ring in the accent colour, mirroring the quiz runtime spinner. */
.spinner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid var(--color-accent-soft);
  border-top-color: var(--color-accent);
  animation: spinner-rotate 0.8s linear infinite;
}

@keyframes spinner-rotate {
  to { transform: rotate(360deg); }
}

/* Animated trailing dots after the label. */
.loading-dots i {
  font-style: normal;
  opacity: 0;
  animation: loading-dot 1.4s infinite both;
}
.loading-dots i:nth-child(2) { animation-delay: 0.2s; }
.loading-dots i:nth-child(3) { animation-delay: 0.4s; }

@keyframes loading-dot {
  0%   { opacity: 0; }
  40%  { opacity: 1; }
  100% { opacity: 0; }
}

/* Respect reduced-motion: drop the animation, keep a static indicator. */
@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation: none;
    border-top-color: var(--color-accent-soft);
  }
  .loading-dots i {
    animation: none;
    opacity: 1;
  }
}

.error-banner {
  font-size: 13px;
  color: #6B4A0F;
  background: rgba(232, 163, 61, 0.18);
  border: 0.5px solid rgba(232, 163, 61, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 12px;
}

/* ── Temporal grouping ── */
.survey-group {
  margin-bottom: 1.75rem;
}

.survey-group:last-child {
  margin-bottom: 0;
}

.group-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 0.6rem;
}

.group-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text-primary);
}

.group-count {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-tertiary);
  background: rgba(0, 0, 0, 0.05);
  border-radius: 999px;
  padding: 1px 8px;
}

.group-hint {
  font-size: 12px;
  color: var(--color-text-tertiary);
}

/* One dot per group, carrying the state at a glance. Only "collecting"
   pulses, because it is the only one that is happening right now. */
.group-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: 0 0 auto;
  align-self: center;
  background: var(--color-text-tertiary);
}

.dot-collecting {
  background: #2E7D32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.16);
  animation: collecting-pulse 2.4s ease-in-out infinite;
}

.dot-scheduled { background: #B8860B; }
.dot-draft { background: rgba(0, 0, 0, 0.25); }
.dot-finished { background: rgba(0, 0, 0, 0.18); }

@keyframes collecting-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.16); }
  50%      { box-shadow: 0 0 0 5px rgba(46, 125, 50, 0.07); }
}

@media (prefers-reduced-motion: reduce) {
  .dot-collecting { animation: none; }
}

.survey-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.survey-row {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 14px 18px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
}

.survey-row:hover {
  border-color: var(--color-accent);
  background: var(--color-bg-soft);
}

.survey-row:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.row-main {
  flex: 1;
  min-width: 0;
}

.row-name {
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.row-slug {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text-tertiary);
  letter-spacing: 0.01em;
}

.row-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.status-pill {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 3px 9px;
  border-radius: 4px;
  white-space: nowrap;
}

.status-pill.status-draft {
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

.status-pill.status-active {
  background: var(--color-accent-soft);
  color: var(--color-accent-dark);
}

.status-pill.status-closed {
  background: var(--color-bg-soft);
  color: var(--color-text-secondary);
  border: 0.5px solid var(--color-border);
}

.status-pill.status-archived {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
}

.row-window {
  font-size: 12px;
  color: var(--color-text-tertiary);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.row-date {
  font-size: 12px;
  color: var(--color-text-tertiary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  min-width: 96px;
  text-align: right;
}

.row-chevron {
  color: var(--color-text-tertiary);
  font-size: 18px;
  line-height: 1;
}

.survey-row:hover .row-chevron {
  color: var(--color-accent);
}

@media (max-width: 720px) {
  .survey-row {
    flex-wrap: wrap;
  }
  .row-meta {
    width: 100%;
    justify-content: flex-start;
    margin-top: 6px;
    flex-wrap: wrap;
  }
  .row-date {
    flex: 1;
    text-align: left;
    min-width: 0;
  }
}
</style>
